import { ApostleFilterInput, TicketMintEnum } from '@apeiron/apostle'
import {
  ActivityType,
  CheckboxFilter,
  FilterInput,
  ListingStatus,
  SlideFilter
} from '@apeiron/common'
import { Box, styled } from '@mui/material'
import { FilterCheckboxGroup } from '@src/components/planet/filter/filterCheckbox/FilterCheckboxGroup'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import ComponentBlocker from '@src/components/share/generic/ComponentBlocker'
import { ComponentBlock } from '@src/constants/componentBlock'
import useFetchApostleFilterJson from '@src/hooks/json/useFetchApostleFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const FILTER_FIELDS: (keyof ApostleFilterInput)[] = [
  'reforgeCountMax',
  'ivMax',
  'ivMin',
  'reforgeCountMin',
  'skillLevelMin',
  'skillLevelMax',
  'nftListingStatus',
  'ticketMint'
]

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Form = styled(BlurForm)`
  > * + * {
    margin-top: 16px;
  }
`

const CheckboxGroupContainer = styled(Box)`
  padding-right: 5px;
`

const ApostleOtherFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const { data: filterSetting } = useFetchApostleFilterJson()

  const [slideFilters, setSlideFilters] = useState<SlideFilter[]>([])

  useEffect(() => {
    if (R.isNotNil(filterSetting)) {
      setSlideFilters(filterSetting.others)
    }
  }, [filterSetting])

  const onPurchaseTypes = useMemo((): CheckboxFilter<ActivityType>[] => {
    return [
      {
        name: 'bounty',
        label: t('apostle.filter.purchase.bounty'),
        value: ActivityType.ApostleBounty
      },
      {
        name: 'bought',
        label: t('apostle.filter.purchase.market'),
        value: ActivityType.Buy
      }
    ]
  }, [t])

  const onSaleItems = useMemo((): CheckboxFilter<ListingStatus>[] => {
    return [
      {
        name: 'forSale',
        label: t('filter.sections.others.status.for_sale'),
        value: ListingStatus.LISTING
      },
      {
        name: 'notForSale',
        label: t('filter.sections.others.status.not_for_sale'),
        value: ListingStatus.UNLISTING
      }
    ]
  }, [t])

  const onSpecialItems = useMemo((): CheckboxFilter<TicketMintEnum>[] => {
    return [
      {
        name: 'origin',
        label: t('filter.sections.others.special.origin'),
        value: TicketMintEnum.TicketOrigin
      }
    ]
  }, [t])

  const numberOfFilters = useMemo(() => {
    const countFilters = R.pipe<any, any, Object, unknown[][], number>(
      R.reject(R.isNil),
      R.pick(FILTER_FIELDS),
      R.toPairs,
      R.length
    )

    const numberOfSlotFilters = countFilters(filterInput)

    return numberOfSlotFilters
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(FILTER_FIELDS, filterInput))
  }, [filterInput, onFilterChange])

  const handleOnValueChange = useCallback(
    (filter: FilterInput) => {
      onFilterChange(filter as ApostleFilterInput)
    },
    [onFilterChange]
  )

  const handleOnActivityChange = useCallback(
    (input?: ActivityType[]) => {
      onFilterChange(
        R.assoc<ActivityType[] | undefined, ApostleFilterInput, string>(
          'activityType',
          input,
          filterInput
        )
      )
    },
    [filterInput, onFilterChange]
  )

  const handleOnSaleChange = useCallback(
    (input?: ListingStatus[]) => {
      onFilterChange(
        R.assoc<ListingStatus[] | undefined, ApostleFilterInput, string>(
          'nftListingStatus',
          input,
          filterInput
        )
      )
    },
    [filterInput, onFilterChange]
  )

  const handleOnSpecialChange = useCallback(
    (input?: TicketMintEnum[]) => {
      onFilterChange(
        R.assoc<TicketMintEnum[] | undefined, ApostleFilterInput, string>(
          'ticketMint',
          input,
          filterInput
        )
      )
    },
    [filterInput, onFilterChange]
  )

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('common.others')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        {!R.isEmpty(slideFilters) && (
          <Form>
            {R.map(
              (filter: SlideFilter) => (
                <RangeSlider
                  decimalScale={filter.decimalScale}
                  filterOptions={filterInput}
                  key={filter.name}
                  labelIconURL={filter.image}
                  labelText={filter.name}
                  marks={false}
                  max={filter.max.slot}
                  maxValueKey={filter.max.field}
                  min={filter.min.slot}
                  minValueKey={filter.min.field}
                  step={filter.step ? filter.step : undefined}
                  onFilterOptionsChange={handleOnValueChange}
                />
              ),
              slideFilters
            )}
          </Form>
        )}
        <CheckboxGroupContainer>
          <ComponentBlocker blockKey={ComponentBlock.ApostleFilterPurchaseType}>
            <FilterCheckboxGroup
              label={t('apostle.filter.purchase_type')}
              items={onPurchaseTypes}
              value={filterInput.activityType || []}
              onChange={handleOnActivityChange}
            />
          </ComponentBlocker>
          <FilterCheckboxGroup
            label={t('filter.sections.others.special.label')}
            items={onSpecialItems}
            value={filterInput.ticketMint || []}
            onChange={handleOnSpecialChange}
          />
          <ComponentBlocker blockKey={ComponentBlock.ApostleFilterStatus}>
            <FilterCheckboxGroup
              label={t('filter.sections.others.status.label')}
              items={onSaleItems}
              value={filterInput.nftListingStatus || []}
              onChange={handleOnSaleChange}
            />
          </ComponentBlocker>
        </CheckboxGroupContainer>
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: ApostleFilterInput
  onFilterChange: (input: ApostleFilterInput) => void
}

export default ApostleOtherFilter
